import { useRef, useState } from 'react';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useClickOutside } from '@/hooks/useClickOutside';
import { useLocale } from '@/i18n';
import { languageName } from '@/i18n/languages';
import { cn } from '@/lib/cn';
import { pathLocale, stripLocale } from '@/seo/site';
import type { Locale } from '@/types/api';

export type LanguageSwitcherProps = {
  className?: string;
  size?: 'sm' | 'md';
  /** Which edge of the button the menu lines up with: `end` in a top bar, `start` in a form. */
  align?: 'start' | 'end';
};

/**
 * The language menu: a globe and the current code (`EN`), opening a list of every shipped
 * language in its own name. Visible before sign-in too, so someone who cannot read the page can
 * still change it. A choice is saved on this device and, when signed in, on the profile.
 *
 * Number formatting deliberately does **not** follow this switch: digits, grouping and the
 * `%` position use exchange notation in every language (§3.3).
 */
export function LanguageSwitcher({
  className,
  size = 'sm',
  align = 'end',
}: LanguageSwitcherProps): JSX.Element {
  const { t } = useTranslation();
  const { locale, supportedLocales, setLocale } = useLocale();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const choose = (code: Locale) => {
    setOpen(false);
    // On a language address (`/tr/…`) another language leaves for the page's root address, so
    // the address and the text agree and a reload keeps the choice. Root addresses stay put.
    const addressLocale = pathLocale(pathname);
    if (addressLocale && code !== addressLocale) {
      navigate(`${stripLocale(pathname)}${search}${hash}`, { replace: true });
    }
    void setLocale(code);
  };
  const rootRef = useRef<HTMLDivElement>(null);

  useClickOutside(rootRef, () => setOpen(false), { enabled: open });

  return (
    <div
      ref={rootRef}
      className={cn('relative', className)}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) setOpen(false);
      }}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t('settings.language')}: ${languageName(locale)}`}
        title={languageName(locale)}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-sm border border-border px-2',
          'text-ink-secondary transition-colors duration-fast ease-out',
          'hover:bg-wash-hover hover:text-ink',
          'focus-visible:outline-none focus-visible:shadow-focus',
          size === 'md' ? 'h-control text-body' : 'h-7 text-caption',
        )}
      >
        <Globe aria-hidden="true" strokeWidth={1.5} className="h-3.5 w-3.5 shrink-0" />
        <span className="font-medium tracking-wide">{locale.split('-')[0].toUpperCase()}</span>
        <ChevronDown aria-hidden="true" strokeWidth={1.5} className="h-3 w-3 shrink-0 opacity-70" />
      </button>

      {open && (
        <div
          role="menu"
          aria-label={t('settings.language')}
          className={cn(
            'absolute top-[calc(100%+6px)] z-popover min-w-[12rem] animate-layer-in',
            'overflow-hidden rounded-md border border-border bg-surface-3 py-1 shadow-popover',
            align === 'end' ? 'right-0' : 'left-0',
          )}
        >
          {supportedLocales.map((code) => {
            const active = code === locale;
            return (
              <button
                key={code}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                lang={code}
                onClick={() => choose(code)}
                className={cn(
                  'flex h-control w-full items-center gap-2 px-3 text-body',
                  'transition-colors duration-fast ease-out hover:bg-wash-hover',
                  'focus-visible:outline-none focus-visible:shadow-focus',
                  active ? 'text-ink' : 'text-ink-secondary hover:text-ink',
                )}
              >
                <span className="w-7 shrink-0 text-left text-caption text-ink-tertiary">
                  {code.split('-')[0].toUpperCase()}
                </span>
                <span className="min-w-0 flex-1 truncate text-left">{languageName(code)}</span>
                {active && (
                  <Check aria-hidden="true" strokeWidth={1.5} className="h-4 w-4 shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
